import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import {
  createCognitoUser,
  deleteCognitoUser,
  editCognitoUser,
  getCognitoUsers,
  resendCognitoInvitation,
} from './thunks'

const initialState: CognitoUserState = {
  status: 'idle',
  error: null,
  users: [],
  searchResults: [],
  nextToken: undefined,
}

const slice = createSlice({
  name: 'cognitoUser',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createCognitoUser.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(createCognitoUser.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(createCognitoUser.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.users = [{ ...action.payload }, ...state.users]
      })

      .addCase(getCognitoUsers.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getCognitoUsers.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getCognitoUsers.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.users = action.payload.cognitoUsers || []
        state.nextToken = action.payload.nextToken
      })

      .addCase(deleteCognitoUser.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(deleteCognitoUser.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteCognitoUser.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const usernameToRemove = action.meta.arg.username
        state.users = state.users.filter((user) => user.username !== usernameToRemove)
      })

      .addCase(resendCognitoInvitation.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(resendCognitoInvitation.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(resendCognitoInvitation.fulfilled, (state) => {
        state.status = 'succeeded'
      })

      .addCase(editCognitoUser.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(editCognitoUser.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(editCognitoUser.fulfilled, (state, action) => {
        state.status = 'succeeded'

        const updatedUser = action.meta.arg
        state.users = state.users.map((user) =>
          user.username === updatedUser.username
            ? {
                ...user,
                email: updatedUser.email ?? user.email,
                phoneNumber: updatedUser.phoneNumber ?? user.phoneNumber,
                ggbUserId: updatedUser.internalUserId?.toString() ?? user.ggbUserId,
              }
            : user,
        )
      })
  },
})

export const selectCognitoUsers = (state: RootState): CognitoUserState => state.cognitoUsers

export default slice.reducer
